import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as Y from 'yjs'
import type { EditorUndoRedo } from '../client/editor-registry.js'

/**
 * Real undo/redo for the editors backed by a collaborative Y.Text — the HTML,
 * markdown and script surfaces, and with them .html .md .txt .json .py .sh.
 *
 * These had no undo of their own. The browser's native textarea undo looked
 * like one, but TextModifier assigns `textarea.value` imperatively whenever
 * the document changes underneath it, which throws that history away — so it
 * worked until a collaborator typed, a file reloaded, or a design-mode edit
 * landed, and then silently didn't. Ctrl+Z is routed here instead, inside the
 * textarea as well as outside it, so there is one history rather than two
 * that disagree.
 *
 * ONLY YOUR OWN EDITS. Y.UndoManager tracks transactions by origin and
 * defaults to the null origin, which is what a local edit carries; useYjs
 * applies everything from the network under 'server' / 'offline-restore', so
 * those are untracked. Undo can never reverse a collaborator's work — the one
 * behaviour that would make this feature worse than having none.
 *
 * Design-mode edits come along for free: HtmlEditor writes them through
 * `doc.transact` on the same Y.Text, so a colour change or a drag-resize is
 * an ordinary undoable step.
 *
 * `captureTimeout` groups changes that land close together, so a burst of
 * typing — or one drag, whose style writes are debounced — undoes as a single
 * step instead of character by character.
 */

/**
 * Which history step a keystroke in an editor's own text box asks for.
 *
 * The textarea claims these itself (see above), so anything it does not claim
 * reaches nobody: the drive's global listener skips text fields on purpose.
 * Ctrl+Y — the Windows redo — was missing, so redo inside a document did
 * nothing on Windows. Matches UndoProvider's listener key for key; the
 * design-mode iframe carries a copy (`undoShortcut` in design-mode-script.ts),
 * and one test table holds all of them to the same answers.
 *
 * `altKey` first: Windows reports AltGr as Ctrl+Alt, so on a Polish layout
 * AltGr+Z is the letter ż, never an undo (#829).
 */
export function textUndoShortcut(
  e: Pick<KeyboardEvent, 'key' | 'ctrlKey' | 'metaKey' | 'altKey' | 'shiftKey'>,
): 'undo' | 'redo' | null {
  if (!(e.ctrlKey || e.metaKey) || e.altKey) return null
  const key = e.key.toLowerCase()
  if (key === 'z') return e.shiftKey ? 'redo' : 'undo'
  if (key === 'y' && !e.shiftKey) return 'redo'
  return null
}

/** Grouping window for consecutive edits (ms). Yjs's own default is 500;
 *  400 keeps a fast typist's run together while still splitting a pause. */
const CAPTURE_TIMEOUT_MS = 400

export interface YTextUndoHandle {
  /** Apply undo. Resolves false when there was nothing to undo.
   *
   *  Async because it runs the SAME sequence as the registered handler —
   *  settle first, apply, then report. Undo arrives by four routes (the
   *  textarea, the preview iframe's relay, the global shortcut, the Edit
   *  menu) and the last two go straight to the registration, so anything a
   *  host needs to happen around an undo has to live in one place or it
   *  silently covers two routes out of four. */
  undo: () => Promise<boolean>
  redo: () => Promise<boolean>
  /**
   * Close the current step so the next change starts a new one.
   *
   * Call it before a DESIGN-mode source write. Those writes rewrite exactly
   * one element's start tag, and the preview's in-place patch engine only
   * accepts a change that falls inside a single element — so two of them
   * merged into one undo step make the revert span both, get refused
   * ('text-child-overlap'), and fall back to a full preview reload: a ~1.5s
   * wait and a visible flash where an instant patch belonged. Keeping each
   * design edit its own step also matches what the user did — three colour
   * changes should take three undos, not vanish in one.
   *
   * Typing is untouched: it never goes through the design commit path, so it
   * keeps grouping on captureTimeout.
   */
  stopCapturing: () => void
}

export interface YTextUndoOptions {
  /** False for a read-only or version-preview mount: it registers nothing, so
   *  Ctrl+Z goes to the drive's file operations instead of dying here. */
  enabled: boolean
  /**
   * True while the editor is REFUSING edits but still on screen — a sync
   * conflict awaiting the user's choice, or a reload in flight. The textarea
   * already drops its onChange/onKeyDown in these states.
   *
   * NOT the same as `enabled: false`. Unregistering would hand Ctrl+Z to the
   * drive's file history, so a keystroke meant for a document the editor is
   * refusing to change would restore a file deleted minutes ago. Suspending
   * keeps the claim and reports an empty history: the keystroke stops here,
   * does nothing, and the Edit menu greys out — the same answer the textarea
   * gives, through the three routes that never touch the textarea.
   *
   * The history itself is KEPT. These states resolve, and the user's typing
   * from before the conflict is still theirs to take back afterwards.
   */
  suspended?: boolean
  /**
   * The SYNCHRONOUS companion to `bulkUpdating` — `useYjs` exposes both.
   *
   * Needed because settling can take up to a second (see `beforeApply`), and
   * a replacement arriving in that window has not reached React state yet:
   * the boolean below still reads false while the document underneath has
   * already been swapped. Re-read after the wait, exactly like the manager
   * and the suspension.
   */
  bulkUpdatingRef?: { current: boolean }
  /** True while the document is being REPLACED wholesale — Discard changes, an
   *  agent rewrite. The steps we hold describe text that no longer exists, and
   *  applying one would splice it back into the new document. */
  bulkUpdating?: boolean
  /**
   * Awaited before every undo and redo, however it was triggered.
   *
   * The HTML editor debounces design-mode style writes by 350ms, and a step
   * still on its way is not yet a step: undoing past it reverses the one
   * before and then lets the style land on top. Settling here rather than at
   * the call sites is the point — the keystroke arrives through the textarea
   * handler, the preview iframe's relay, the global shortcut AND the Edit
   * menu, and the last two go straight to the registered handler.
   */
  beforeApply?: () => Promise<void>
  /** Called after every undo and redo with whether anything actually moved.
   *  `false` means the stack was empty — a host arming one-shot state (the
   *  preview's expedited reload) must not arm it for a keystroke that changed
   *  nothing. */
  afterApply?: (applied: boolean) => void
}

export function useYTextUndo(
  doc: Y.Doc | null,
  ytext: Y.Text | null,
  registerUndoRedo: ((editor: EditorUndoRedo) => () => void) | undefined,
  { enabled, suspended = false, bulkUpdating = false, bulkUpdatingRef, beforeApply, afterApply }: YTextUndoOptions,
): YTextUndoHandle {
  const beforeApplyRef = useRef(beforeApply)
  beforeApplyRef.current = beforeApply
  const afterApplyRef = useRef(afterApply)
  afterApplyRef.current = afterApply
  const managerRef = useRef<Y.UndoManager | null>(null)
  const suspendedRef = useRef(suspended)
  suspendedRef.current = suspended
  const applyingRef = useRef(false)
  const bulkRef = useRef(bulkUpdatingRef)
  bulkRef.current = bulkUpdatingRef
  // The registration effect captures this once; `apply` is defined below it.
  const applyRef = useRef<(dir: 'undo' | 'redo') => Promise<boolean>>(async () => false)
  const [manager, setManager] = useState<Y.UndoManager | null>(null)

  // The manager's LIFETIME is tied to the document, never to the registration.
  // TabContent hands `registerUndoRedo` only to the ACTIVE tab and swaps it for
  // undefined the moment the tab loses focus (MiniAppEditor does the same
  // between its Run and Settings faces). Keying this effect on it would destroy
  // the manager on every such switch: come back to the tab and your typing
  // history is gone, the editor reports nothing to undo, and Ctrl+Z falls
  // through to the drive — the user asks for their sentence back and a file
  // they deleted minutes ago reappears instead.
  useEffect(() => {
    if (!doc || !ytext || !enabled) return
    const m = new Y.UndoManager(ytext, { captureTimeout: CAPTURE_TIMEOUT_MS })
    managerRef.current = m
    setManager(m)
    return () => {
      m.destroy()
      if (managerRef.current === m) managerRef.current = null
      setManager(null)
    }
  }, [doc, ytext, enabled])

  // Registration comes and goes with the tab's focus; the history outlives it.
  useEffect(() => {
    if (!registerUndoRedo || !enabled) return

    // BEFORE the document arrives, claim the keystroke anyway.
    //
    // In local (share-pointer) mode the Y.Doc is null until the file loads,
    // and the host has already put the user in the editor region by then. An
    // editor that registers nothing reads as a viewer, so Ctrl+Z in that
    // window left the tab and restored a file the user deleted earlier. The
    // stub does nothing and greys the menu out, which is the honest answer
    // for a document that is not on screen yet.
    if (!manager) {
      return registerUndoRedo({
        editable: true,
        undo: async () => false,
        redo: async () => false,
        onStateChange: (cb) => { cb({ canUndo: false, canRedo: false }); return () => {} },
      })
    }

    let notify: ((s: { canUndo: boolean; canRedo: boolean }) => void) | null = null
    const publish = () => notify?.({
      canUndo: !suspended && manager.undoStack.length > 0,
      canRedo: !suspended && manager.redoStack.length > 0,
    })

    manager.on('stack-item-added', publish)
    manager.on('stack-item-updated', publish)
    manager.on('stack-item-popped', publish)
    manager.on('stack-cleared', publish)

    const unregister = registerUndoRedo({
      // The whole point of this hook is that the document can be CHANGED
      // here, so Ctrl+Z belongs to it while the user is in it — including the
      // moments its stack is empty (a fresh open, everything already undone,
      // the preview face with no textarea). Without this the keystroke walked
      // past an open document into the drive and reversed a file operation
      // the user was not looking at.
      editable: true,
      // Only actually yields when there IS something to settle: awaiting an
      // absent hook would push every undo onto a microtask and change the
      // timing for the editors that have no debounced writes at all.
      undo: () => applyRef.current('undo'),
      redo: () => applyRef.current('redo'),
      onStateChange: (cb) => {
        notify = cb
        publish()
        return () => { notify = null }
      },
    })

    return () => {
      manager.off('stack-item-added', publish)
      manager.off('stack-item-updated', publish)
      manager.off('stack-item-popped', publish)
      manager.off('stack-cleared', publish)
      unregister()
    }
    // `suspended` is in here so the Edit menu follows it. Re-registering is
    // the same cheap swap a tab switch already does, and the manager — the
    // history itself — has its own lifetime above.
  }, [manager, registerUndoRedo, suspended, enabled])

  // A wholesale replacement invalidates every step at once: those inverses
  // reinsert text the replacement removed, which is not an undo of anything
  // the user did. Cleared on the way IN so nothing can be applied mid-swap.
  useEffect(() => {
    if (bulkUpdating) managerRef.current?.clear()
  }, [bulkUpdating])

  /** The ONE sequence every route runs. Kept here rather than at the call
   *  sites because two of the four routes never reach them. */
  const run = useCallback(async (dir: 'undo' | 'redo'): Promise<boolean> => {
    if (!managerRef.current) return false
    // Checked here rather than only at the registration: the textarea and the
    // preview iframe's relay call this handle directly.
    if (suspendedRef.current) {
      afterApplyRef.current?.(false)
      return false
    }
    // Only actually yields when there IS something to settle: awaiting an
    // absent hook would push every undo onto a microtask and change the
    // timing for the editors that have no debounced writes at all.
    const pending = beforeApplyRef.current?.()
    if (pending) await pending
    // Re-read AFTER the await. Settling a design edit can wait up to a second
    // on the preview, and a collab reconnect in that window swaps the Y.Doc
    // for a new one — applying to the destroyed manager would edit a document
    // that is no longer on screen.
    const manager = managerRef.current
    if (!manager) return false
    // Re-read these too, for the same reason: settling can wait up to a second
    // on the preview, and a sync conflict — or a wholesale replacement, which
    // is worse, because our steps then describe text the replacement removed
    // — can arrive in that window. The replacement is checked through the
    // synchronous ref, since React has not rendered the state version yet.
    if (suspendedRef.current || bulkRef.current?.current) {
      afterApplyRef.current?.(false)
      return false
    }
    const stack = dir === 'undo' ? manager.undoStack : manager.redoStack
    if (stack.length === 0) {
      afterApplyRef.current?.(false)
      return false
    }
    if (dir === 'undo') manager.undo()
    else manager.redo()
    afterApplyRef.current?.(true)
    return true
  }, [])

  /**
   * One at a time, like useDbUndo.
   *
   * Holding Ctrl+Z down repeats the key every ~30ms, while settling a design
   * edit can take up to a second. Without this a dozen runs interleave: they
   * report `afterApply(true)` out of order and arm the preview's expedited
   * reload against content that has moved on again. A refused repeat costs
   * the user one keypress.
   */
  const apply = useCallback(async (dir: 'undo' | 'redo'): Promise<boolean> => {
    if (applyingRef.current) return false
    applyingRef.current = true
    try {
      return await run(dir)
    } finally {
      applyingRef.current = false
    }
  }, [run])

  applyRef.current = apply

  const undo = useCallback(() => apply('undo'), [apply])
  const redo = useCallback(() => apply('redo'), [apply])

  const stopCapturing = useCallback(() => {
    managerRef.current?.stopCapturing()
  }, [])

  // Stable, like useDbUndo's — callers may reasonably put it in a dependency
  // array, and a fresh object each render would re-run whatever it guards.
  return useMemo(() => ({ undo, redo, stopCapturing }), [undo, redo, stopCapturing])
}
